import { Compass, Wind, Anchor, Award, Users } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CtaBanner } from "@/components/CtaBanner";
import { Reveal, Overline } from "@/components/motion";
import { IMAGES } from "@/data/site";

const MODULES = [
  {
    nr: "01",
    title: "Inițiere",
    days: "2 zile",
    desc: "Primul contact cu velierul: terminologie nautică, noduri esențiale, reguli de siguranță, ridicarea și strângerea pânzelor, mersul la vânt.",
    topics: ["Terminologie & siguranță", "Noduri marinărești", "Pânze și direcția vântului"],
  },
  {
    nr: "02",
    title: "Perfecționare",
    days: "3 zile",
    desc: "Manevre reale la cârmă: viraje, oforare, omul peste bord, acostarea și andocarea în port — partea care desparte pasagerul de skipper.",
    topics: ["Viraje & oforare", "Acostare și andocare", "Manevră sub motor"],
  },
  {
    nr: "03",
    title: "Navigație Costieră",
    days: "4 zile",
    desc: "Planificarea rutei, citirea hărților, meteo, navigație nocturnă și pregătire intensivă pentru examenul de permis ANR categoria D.",
    topics: ["Hărți & rută", "Meteo & navigație nocturnă", "Pregătire examen ANR D"],
  },
];

const WHY = [
  { icon: Award, text: "Skipper-instructor cu peste 15 ani de navigație pe Marea Neagră" },
  { icon: Users, text: "Maxim 4 cursanți la bord — fiecare stă la cârmă, nu la cafeluță" },
  { icon: Anchor, text: "Antrenament real în port și în larg, pe un velier clasic de 13 metri" },
  { icon: Compass, text: "Curriculum aliniat cerințelor Autorității Navale Române" },
];

const Skipper = () => (
  <div data-testid="skipper-page">
    <PageHero
      testid="skipper-hero"
      image={IMAGES.training}
      alt="Cârma unui velier în timpul unei lecții practice de navigație"
      overline="Școala de Navigație"
      titleLines={["Devino skipper.", "La propriu."]}
      subtitle="Cursuri practice la bordul unui Sun Odyssey 42.2 — de la primul nod până la pregătirea pentru permisul de navigație. Teoria o facem pe ponton, restul pe mare."
    />

    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
      <Reveal className="mb-16">
        <Overline className="mb-6">Curriculum</Overline>
        <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl lg:text-5xl">
          Trei module. <span className="italic text-gold">O singură cârmă — a ta.</span>
        </h2>
      </Reveal>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {MODULES.map((mod, i) => (
          <Reveal key={mod.nr} delay={i * 0.12}>
            <div className="flex h-full flex-col border border-white/10 p-8 transition-colors duration-500 hover:border-gold/40 lg:p-10">
              <div className="flex items-baseline justify-between">
                <p className="font-serif text-5xl font-light text-gold/40">{mod.nr}</p>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{mod.days}</p>
              </div>
              <h3 className="mt-8 font-serif text-2xl text-ivory">{mod.title}</h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-mist">{mod.desc}</p>
              <ul className="mt-8 space-y-3 border-t border-white/10 pt-6">
                {mod.topics.map((topic) => (
                  <li key={topic} className="flex items-center gap-3 text-xs uppercase tracking-[0.15em] text-ivory/60">
                    <Wind className="h-3 w-3 text-gold" /> {topic}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <p className="mt-12 border border-gold/30 p-8 text-center font-serif text-2xl font-light text-ivory lg:p-10">
          Modulele se pot parcurge separat sau împreună, ca pachet complet —{" "}
          <span className="text-gold">de la 100 € / modul / persoană.</span>
        </p>
      </Reveal>
    </section>

    <section className="border-y border-white/10 bg-deep">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <Reveal className="mb-16">
          <Overline className="mb-6">De Ce cu Noi</Overline>
          <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl">
            Aici nu înveți despre mare. <span className="italic text-gold">Înveți marea.</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2">
          {WHY.map((item, i) => (
            <Reveal key={item.text} delay={i * 0.1}>
              <div className="flex items-start gap-6 border-t border-white/10 pt-8">
                <item.icon className="mt-1 h-6 w-6 shrink-0 text-gold" />
                <p className="text-base leading-relaxed text-ivory/80">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CtaBanner
      title="Sezonul 2027 începe cu primul modul."
      subtitle="Grupele se formează din primăvară. Sună-ne și îți rezervăm locul la cârmă."
    />
  </div>
);

export default Skipper;
